// Forecast Category — sales pipeline rollup buckets (Pipeline / Best Case / Commit).
//
// Stored values are the source CRM's raw API values (Salesforce ForecastCategory:
// Pipeline/BestCase/Forecast/Closed/Omitted; HubSpot hs_manual_forecast_category
// internal values), so display normalizes through a canonical map. The backend
// roll-up buckets mirror this map in coffee/src/python/crm/api/deal_aggregate_rollups.py
// — keep the two in sync.

export const FORECAST_CATEGORIES = ["Pipeline", "Best Case", "Commit"] as const;
export type ForecastCategory = typeof FORECAST_CATEGORIES[number];

// "closed" maps to Commit for display only — closed-won deals always count toward
// both rollups regardless of category. "omitted"/unknown map to null (uncategorized).
// SFDC's fifth category MostLikely sits between Best Case and Commit; we fold it
// into Best Case to match SFDC's cumulative rollup (Best Case includes Most Likely).
const RAW_VALUE_MAP: Record<string, ForecastCategory> = {
  "pipeline": "Pipeline",
  "best case": "Best Case",
  "bestcase": "Best Case",
  "best_case": "Best Case",
  "mostlikely": "Best Case",
  "most likely": "Best Case",
  "commit": "Commit",
  "forecast": "Commit", // SFDC's API name for its commit bucket
  "closed": "Commit",
};

/** Canonical category for a stored raw value, or null when uncategorized. */
export function normalizeForecastCategory(raw: string | null | undefined): ForecastCategory | null {
  const v = (raw || "").trim().toLowerCase();
  if (!v) return null;
  return RAW_VALUE_MAP[v] ?? null;
}

/** Stage-name heuristic used only in Lovable preview (dummy data has recognizable
 *  stage names). Production shows uncategorized deals as "—" instead. */
export function defaultForecastCategory(stage: string): ForecastCategory {
  if (!stage || stage === "-") return "Pipeline";
  if (/closed won/i.test(stage)) return "Commit";
  if (/closed lost/i.test(stage)) return "Pipeline";
  if (/negotiation/i.test(stage)) return "Commit";
  if (/proposal/i.test(stage)) return "Best Case";
  return "Pipeline";
}

/** Category to display for a deal row: the normalized stored value when present,
 *  else the stage heuristic in preview, else null (rendered as "—"). */
export function displayForecastCategory(
  d: { stage: string; forecastCategory?: string },
  { useHeuristic }: { useHeuristic: boolean }
): ForecastCategory | null {
  const stored = normalizeForecastCategory(d.forecastCategory);
  if (stored) return stored;
  return useHeuristic ? defaultForecastCategory(d.stage) : null;
}

/** Client-side rollup over loaded deals — preview/dummy-data path only; production
 *  uses the server-side bestCase/commit aggregates from getDealsAggregates.
 *  Closed Won always counts toward Commit; Best Case is inclusive of Commit
 *  (standard SFDC rollup). Closed Lost is excluded. */
export function computeForecastRollups(
  deals: Array<{ stage: string; amount: number; forecastCategory?: string }>,
  { useHeuristic }: { useHeuristic: boolean }
): { commitTotal: number; bestCaseTotal: number } {
  let commit = 0;
  let bestCase = 0;
  for (const d of deals) {
    if (d.stage === "Closed Lost") continue;
    if (d.stage === "Closed Won") { commit += d.amount; bestCase += d.amount; continue; }
    const fc = displayForecastCategory(d, { useHeuristic });
    if (fc === "Commit") { commit += d.amount; bestCase += d.amount; }
    else if (fc === "Best Case") { bestCase += d.amount; }
  }
  return { commitTotal: commit, bestCaseTotal: bestCase };
}
